import React, { useState, useCallback, useEffect, useRef } from 'react';
import { NodeViewWrapper, NodeViewProps } from '@tiptap/react';
import axios from 'axios';
import ReactDOM from 'react-dom';
import { api } from '../../services/api';

// Стили компонента
import './KanbanBoardComponent.css';

// Определяем типы импортов для JSX-файлов
declare module '*.jsx' {
  const content: any;
  export default content;
}

// Импортируем компоненты
import DraggableCard from './DraggableCard';
import DroppableColumn from './DroppableColumn';

// Глобальная карта для отслеживания уже созданных ID канбанов
// Используем WeakMap для автоматической очистки при уничтожении объектов
const initializedKanbanIds = new Map<string, boolean>();

// Флаг для включения дополнительных функций (тестовой карточки)
const PJSON_FLAG = false;

// Типы для состояния доски
interface Card {
  id: string;
  title: string;
  description: string;
  priority?: 'low' | 'medium' | 'high';
  deadline?: string;
}

interface Column {
  id: string;
  title: string;
  cardIds: string[];
  isCompletedColumn?: boolean;
}

interface BoardState {
  columns: Column[];
  cards: { [key: string]: Card };
  columnOrder: string[];
  boardTitle?: string;
  _lastUpdate?: string; // Добавляем поле для отслеживания последнего обновления
  updateCounter?: number; // Добавляем поле для счетчика обновлений
}

// Вспомогательная функция для генерации ID
const generateId = (): string => `id-${Math.random().toString(36).substr(2, 9)}`;

// Функция для сравнения двух состояний канбана
const areStatesEqual = (state1: BoardState, state2: BoardState): boolean => {
  try {
    return JSON.stringify(state1) === JSON.stringify(state2);
  } catch (e) {
    return false;
  }
}

// Интерфейс для данных перетаскивания
interface DragData {
  type: string;
  pos: number;
  nodeSize: number;
  node?: any; // JSON представление узла
}

// Основной компонент канбан-доски
const KanbanBoardComponent: React.FC<NodeViewProps> = (props) => {
  const { node, updateAttributes, editor, getPos } = props;
  
  // Логирование для отладки процесса рендеринга
  console.log(`[КанбанДебаг] Рендеринг компонента KanbanBoardComponent`);
  console.log(`[КанбанДебаг] Тип узла:`, node.type.name);
  console.log(`[КанбанДебаг] ID узла:`, node.attrs.id);
  console.log(`[КанбанДебаг] data-type узла:`, node.attrs['data-type']);
  console.log(`[КанбанДебаг] Все атрибуты узла:`, JSON.stringify(node.attrs));
  
  // Функция для генерации GUID
  const generateGuid = () => {
    return crypto.randomUUID ? crypto.randomUUID() :
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
  };
  
  // Получаем уникальный идентификатор документа
  const documentId = editor?.view?.state?.doc?.attrs?.documentId || 
                    editor?.state?.doc?.attrs?.id ||
                    window.location.pathname.split('/').pop() || 
                    'unknown-doc';

  // Получаем реальный FileId для сохранения на сервер
  const fileId = window.location.pathname.split('/').filter(p => p).pop();
  
  // Получаем ID канбана НАПРЯМУЮ из атрибутов узла Tiptap
  const instanceId = useRef<string | null>(node.attrs.id || null);

  // Валидация ID при инициализации и логирование
  useEffect(() => {
    if (!instanceId.current) {
      console.error(`[Канбан ОШИБКА] Компонент инициализирован без ID в node.attrs.id! Проверьте логику создания узла.`, node.attrs);
      // Генерируем ID для канбана, если он отсутствует
      const newId = `kanban-${generateGuid()}`;
      instanceId.current = newId;
      console.log(`[Канбан] Сгенерирован новый ID для канбана: ${newId}`);
      
      // Обновляем атрибут id в узле, если доступна функция updateAttributes
      if (updateAttributes && typeof updateAttributes === 'function') {
        updateAttributes({ id: newId });
        console.log(`[Канбан ${newId}] Обновлен атрибут id в узле Tiptap`);
      }
    } else {
      console.log(`[Канбан ${instanceId.current}] Инициализирован с ID из node.attrs: ${instanceId.current}`);
    }
    // Эта проверка выполняется только один раз при монтировании
  }, []); // Пустой массив зависимостей

  console.log(`[Канбан] Попытка инициализации с ID: ${instanceId.current}`);
  const styleId = useRef<string>(`kanban-style-${generateId()}`);

  // --- УПРОЩЕННАЯ ЛОГИКА ИНИЦИАЛИЗАЦИИ ---
  // Получаем состояние из распарсенных атрибутов node
  const parsedBoardState = node.attrs['data-state'];

  // Проверка на валидность BoardState
  const isValidBoardState = (state: any): state is BoardState => {
    try {
      // Показываем тип данных для отладки
      console.log(`[Канбан ${instanceId.current}] Проверка валидности состояния, тип:`, typeof state);
      
      // Если state - строка, пробуем ее распарсить
      if (typeof state === 'string') {
        try {
          // Отладка входящей строки JSON
          console.log(`[Канбан ${instanceId.current}] Длина JSON строки: ${state.length} символов`);
          
          // Проверяем на наличие признаков повреждения JSON
          const openingBraces = (state.match(/{/g) || []).length;
          const closingBraces = (state.match(/}/g) || []).length;
          console.log(`[Канбан ${instanceId.current}] Количество открывающих скобок: ${openingBraces}, закрывающих: ${closingBraces}`);
          
          if (openingBraces !== closingBraces) {
            console.warn(`[Канбан ${instanceId.current}] Обнаружено несоответствие скобок в JSON! Возможно, данные повреждены.`);
          }
          
          // Попытка очистить повреждённый JSON
          let cleanJsonStr = state;
          
          // Ищем первую полную JSON структуру, игнорируя возможные дублирования
          const jsonStartPos = cleanJsonStr.indexOf('{');
          if (jsonStartPos > -1) {
            let openBraces = 0;
            let foundValidJson = false;
            let endPos = jsonStartPos;
            
            // Анализируем символы, чтобы найти корректное закрытие JSON
            for (let i = jsonStartPos; i < cleanJsonStr.length; i++) {
              if (cleanJsonStr[i] === '{') openBraces++;
              else if (cleanJsonStr[i] === '}') {
                openBraces--;
                // Когда нашли закрывающую скобку для начального JSON объекта
                if (openBraces === 0) {
                  endPos = i + 1; // включая закрывающую скобку
                  foundValidJson = true;
                    break;
                  }
                }
              }
              
            if (foundValidJson) {
              // Извлекаем только валидную часть JSON
              cleanJsonStr = cleanJsonStr.substring(jsonStartPos, endPos);
              console.log(`[Канбан ${instanceId.current}] Извлечена валидная часть JSON длиной ${cleanJsonStr.length} символов`);
            }
          }
          
          // Теперь пробуем парсить очищенную строку
          state = JSON.parse(cleanJsonStr);
          console.log(`[Канбан ${instanceId.current}] Успешно распарсена строка data-state в объект`);
        } catch (e) {
          console.error(`[Канбан ${instanceId.current}] Ошибка парсинга строки data-state:`, e);
          console.error(`[Канбан ${instanceId.current}] Проблемная строка (первые 100 символов): ${state.substring(0, 100)}...`);
          return false;
        }
      }
      
      // Детальная проверка структуры
      const hasColumns = state && 
                        typeof state === 'object' &&
                        Array.isArray(state.columns);
                        
      const hasCards = state && 
                      typeof state === 'object' &&
                      typeof state.cards === 'object' && 
                      state.cards !== null;
                      
      const hasColumnOrder = state && 
                            typeof state === 'object' &&
                            Array.isArray(state.columnOrder);
      
      // Логируем результаты проверок для отладки
      console.log(`[Канбан ${instanceId.current}] Проверка структуры:`, {
        hasColumns,
        hasCards,
        hasColumnOrder,
        columns: state?.columns ? `${state.columns.length} колонок` : 'нет',
        cards: state?.cards ? `${Object.keys(state.cards).length} карточек` : 'нет',
        columnOrder: state?.columnOrder ? `${state.columnOrder.length} элементов` : 'нет'
      });
      
      return hasColumns && hasCards && hasColumnOrder;
    } catch (e) {
      console.error(`[Канбан ${instanceId.current}] Ошибка проверки валидности состояния:`, e);
      return false;
    }
  };

  // Создаем пустое состояние для случая, когда нет данных
  const emptyBoardState: BoardState = {
    columns: [
      { id: 'col-1', title: 'Планируется', cardIds: [] },
      { id: 'col-2', title: 'В работе', cardIds: [] },
      { id: 'col-3', title: 'Выполнено', cardIds: [], isCompletedColumn: true }
    ],
    cards: {},
    columnOrder: ['col-1', 'col-2', 'col-3'],
    boardTitle: 'Канбан-доска',
    updateCounter: 0
  };

  // Используем валидные данные или пустое состояние
  let parsedState: BoardState = emptyBoardState;
  
  if (isValidBoardState(parsedBoardState)) {
    // Если состояние валидно, используем его (возможно после парсинга из JSON)
    parsedState = typeof parsedBoardState === 'string'
      ? JSON.parse(parsedBoardState)
      : parsedBoardState;
    console.log(`[Канбан ${instanceId.current}] Успешно инициализировано состояние из data-state:`, parsedState);
  } else {
    console.warn(`[Канбан ${instanceId.current}] Полученные данные из атрибутов невалидны или отсутствуют. Используется пустое состояние.`);
    console.warn(`[Канбан ${instanceId.current}] Полученные данные:`, parsedBoardState);
  }
  
  // Финальное состояние для использования
  const initialBoardState: BoardState = parsedState;

  // Если это первичная инициализация с пустыми колонками, добавим тестовую карточку
  // для проверки отображения
  if (isValidBoardState(initialBoardState) &&
      initialBoardState.columnOrder.length > 0) {
    
    // Проверяем общее количество карточек
    const totalCards = Object.keys(initialBoardState.cards).length;
    
    if (totalCards === 0 && PJSON_FLAG) {
      console.log(`[Канбан ${instanceId.current}] Добавляем тестовую карточку для проверки отображения...`);
      
      // Создаем тестовую карточку с уникальным ID
      const testCardId = `card-test-id-${Date.now().toString(36)}`;
      initialBoardState.cards[testCardId] = {
        id: testCardId,
        title: 'Тестовая карточка',
        description: 'Эта карточка добавлена для проверки функциональности канбана',
        priority: 'medium',
        deadline: ''
      };
      
      // Добавляем карточку в первую колонку
      if (initialBoardState.columnOrder.length > 0) {
        const firstColumnId = initialBoardState.columnOrder[0];
        const firstColumn = initialBoardState.columns.find(col => col.id === firstColumnId);
        if (firstColumn) {
          firstColumn.cardIds = [testCardId, ...(firstColumn.cardIds || [])];
          console.log(`[Канбан ${instanceId.current}] Тестовая карточка добавлена в колонку ${firstColumn.title}`);
        }
      }
    } else {
      console.log(`[Канбан ${instanceId.current}] Обнаружены существующие карточки (${totalCards}), тестовая карточка не добавляется`);
    }
  }

  if (isValidBoardState(parsedBoardState)) {
    console.log(`[Канбан ${instanceId.current}] Успешно инициализировано состояние из data-state:`, initialBoardState);
  } else {
    console.warn(`[Канбан ${instanceId.current}] Полученные данные из атрибутов невалидны или отсутствуют. Используется пустое состояние.`);
    console.warn(`[Канбан ${instanceId.current}] Полученные данные:`, parsedBoardState);
  }
  // --- КОНЕЦ УПРОЩЕННОЙ ЛОГИКИ ---

  // Сохраняем исходное состояние для отслеживания изменений
  const [boardState, setBoardState] = useState<BoardState>(initialBoardState);
  const [filterText, setFilterText] = useState('');
  const [filterPriority, setFilterPriority] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  // Делаем глубокое клонирование для избежания ссылочных проблем
  const lastSavedState = useRef<BoardState>(JSON.parse(JSON.stringify(initialBoardState)));
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Состояния для перетаскивания и модального окна
  const [draggedCardId, setDraggedCardId] = useState<string | null>(null);
  const [draggedFromColumnId, setDraggedFromColumnId] = useState<string | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [columnToDelete, setColumnToDelete] = useState<Column | null>(null);
  
  // Добавляем состояние для модального окна подтверждения удаления канбана
  const [showDeleteKanbanModal, setShowDeleteKanbanModal] = useState(false);

  // Добавляем функцию для удаления канбана
  const handleDelete = useCallback(() => {
    // Открываем модальное окно подтверждения вместо немедленного удаления
    setShowDeleteKanbanModal(true);
  }, []);
  
  // Функция для подтверждения удаления канбана
  const confirmDeleteKanban = useCallback(() => {
    if (typeof getPos === 'function' && editor) {
      // Получаем позицию узла в документе
      const pos = getPos();
      
      // Удаляем узел канбана из документа
      editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
    }
    
    // Закрываем модальное окно
    setShowDeleteKanbanModal(false);
  }, [editor, getPos, node]);
  
  // Функция для отмены удаления канбана
  const cancelDeleteKanban = useCallback(() => {
    setShowDeleteKanbanModal(false);
  }, []);
  
  // Состояние для перетаскивания всего канбана
  const [isDragging, setIsDragging] = useState(false);
  
  // Функция начала перетаскивания всего канбана
  const handleKanbanDragStart = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (typeof getPos !== 'function' || !editor) {
      console.warn('Невозможно начать перетаскивание: getPos не является функцией или editor не определен');
      return;
    }
    
    try {
      console.log('[Канбан] Начало перетаскивания канбана');
      e.stopPropagation();
      
      // Добавляем класс к элементу для визуальной индикации
      const target = e.currentTarget.closest('.interactive-kanban-wrapper');
      if (target) {
        target.classList.add('kanban-dragging');
        console.log('[Канбан] Добавлен класс kanban-dragging');
      }
      
      // Получаем позицию узла в документе
      const pos = getPos();
      console.log(`[Канбан] Позиция узла: ${pos}, размер: ${node.nodeSize}`);
      
      // Устанавливаем данные для перетаскивания
      const dragData: DragData = {
        type: 'kanbanBoard',
        pos: pos,
        nodeSize: node.nodeSize
      };
      
      // Получаем узел напрямую из редактора для более надежного перетаскивания
      const editorState = editor.state;
      const nodeAtPos = editorState.doc.nodeAt(pos);
      
      if (nodeAtPos && nodeAtPos.type.name === 'kanbanBoard') {
        // Добавляем сам узел к данным перетаскивания для сохранения его атрибутов
        dragData.node = nodeAtPos.toJSON();
        console.log('[Канбан] Добавлен узел канбана к данным перетаскивания');
      }
      
      // Сериализуем данные
      const serializedData = JSON.stringify(dragData);
      console.log('[Канбан] Данные для перетаскивания:', serializedData);
      
      // Устанавливаем тип операции и данные
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', 'kanbanBoard'); // Для совместимости
      e.dataTransfer.setData('application/tiptap-node', serializedData);
      
      // Проверяем, установлены ли данные
      console.log('[Канбан] Типы данных в dataTransfer:', [...e.dataTransfer.types]);
      
      // Создаем полупрозрачный элемент для перетаскивания
      const dragImage = document.createElement('div');
      dragImage.style.width = '300px';
      dragImage.style.height = '150px';
      dragImage.style.background = 'rgba(59, 130, 246, 0.2)';
      dragImage.style.border = '2px dashed #3b82f6';
      dragImage.style.borderRadius = '8px';
      dragImage.style.padding = '8px';
      dragImage.style.fontSize = '14px';
      dragImage.style.color = '#3b82f6';
      dragImage.innerHTML = '<div style="text-align: center; padding-top: 60px;">Канбан-доска</div>';
      dragImage.style.position = 'absolute';
      dragImage.style.top = '-1000px';
      dragImage.style.left = '0';
      dragImage.style.opacity = '0.7';
      document.body.appendChild(dragImage);
      
      // Устанавливаем изображение для перетаскивания
      e.dataTransfer.setDragImage(dragImage, 150, 75);
      console.log('[Канбан] Установлено изображение для перетаскивания');
      
      // Устанавливаем состояние перетаскивания
      setIsDragging(true);
      
      // Удаляем временный элемент после начала перетаскивания
      setTimeout(() => {
        if (dragImage.parentNode) {
          document.body.removeChild(dragImage);
          console.log('[Канбан] Удален временный элемент drag image');
        }
      }, 100);
    } catch (error) {
      console.error('[Канбан] Ошибка при начале перетаскивания:', error);
    }
  }, [editor, getPos, node]);
  
  // Функция завершения перетаскивания
  const handleKanbanDragEnd = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    console.log('[Канбан] Завершение перетаскивания');
    e.stopPropagation();
    
    // Удаляем класс для визуальной индикации
    const target = e.currentTarget.closest('.interactive-kanban-wrapper');
    if (target) {
      target.classList.remove('kanban-dragging');
      console.log('[Канбан] Удален класс kanban-dragging');
    }
    
    // Сбрасываем состояние перетаскивания
    setIsDragging(false);
  }, []);
  
  // Состояние для отображения кнопки удаления
  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const [showDragHandle, setShowDragHandle] = useState(false);

  // Функции для показа/скрытия элементов управления
  const showControls = useCallback(() => {
    setShowDeleteButton(true);
    setShowDragHandle(true);
  }, []);
  
  const hideControls = useCallback(() => {
    setShowDeleteButton(false);
    setShowDragHandle(false);
  }, []);
  
  // Функция сохранения состояния на сервер
  const saveStateToServer = async (state: BoardState, componentId: string) => {
    // Убрана проверка !componentId, так как она теперь выполняется перед вызовом
    try {
      // Создаем глубокую копию состояния для безопасного изменения
      const stateCopy = JSON.parse(JSON.stringify(state));
      
      // Обновляем счетчик обновлений
      if (typeof stateCopy.updateCounter === 'number') {
        stateCopy.updateCounter += 1;
      } else {
        stateCopy.updateCounter = 1;
      }
      
      // Добавляем отметку о времени последнего обновления
      stateCopy._lastUpdate = new Date().toISOString();
      
      // Использовать правильный URL и формат запроса согласно API
      const apiUrl = '/knowledge/update-component-state';
      console.log(`[Канбан ${componentId}] Отправка состояния на сервер: ${apiUrl}`);
      
      // Сериализуем состояние для отправки на сервер
      const serializedState = JSON.stringify(stateCopy);
      
      // Отправляем запрос с правильной структурой данных через объект api
        const response = await api.post(apiUrl, {
          ComponentId: componentId,
          FileId: fileId,
        DataState: serializedState
      });
      
      console.log(`[Канбан ${componentId}] Состояние успешно сохранено на сервер, ответ:`, response.data);
        return true;
    } catch (error: any) {
       console.error(`[Канбан ${componentId}] Ошибка при сохранении состояния на сервер:`, error);
      
      // Пытаемся по альтернативному URL при ошибке 404
      if (error.response && error.response.status === 404) {
        try {
          console.log(`[Канбан ${componentId}] Пробуем альтернативный URL для сохранения...`);
          // Создаем копию состояния
          const stateCopy = JSON.parse(JSON.stringify(state));
          const altUrl = '/api/knowledge/update-component-state';
          
          // Сериализуем состояние для отправки на сервер
          const serializedState = JSON.stringify(stateCopy);
          
          // Отправляем запрос с правильной структурой данных через объект api
          const altResponse = await api.post(altUrl, {
                ComponentId: componentId,
                FileId: fileId,
            DataState: serializedState
          });
          
          console.log(`[Канбан ${componentId}] Состояние успешно сохранено по альтернативному URL, ответ:`, altResponse.data);
              return true;
            } catch (altError) {
          console.error(`[Канбан ${componentId}] Ошибка при сохранении по альтернативному URL:`, altError);
            }
          }
      
        return false;
      }
  };
  
  // Используем ref для отслеживания последнего времени сохранения
  const lastSaveTimeRef = useRef<number>(0);
  
  // Автосохранение при изменениях
  useEffect(() => {
    // Сначала проверяем наличие ID канбана
    if (!instanceId.current) {
      console.warn(`[Канбан] Автосохранение невозможно: отсутствует ID канбана`);
      // showStatusIndicator("Ошибка: отсутствует ID канбана", 3000); // <-- УДАЛЕНО
      return;
    }
    
    // Отладочный вывод состояния
    // console.log(`[Канбан ${instanceId.current}] useEffect для автосохранения: hasUnsavedChanges=${hasUnsavedChanges}, isSaving=${isSaving}`);
    
    // Если есть несохраненные изменения и не идет процесс сохранения
    if (hasUnsavedChanges && !isSaving && boardState && instanceId.current) {
      // Отменяем предыдущий таймер, если он существует (например, из-за быстрых последовательных изменений)
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
        console.log(`[Канбан ${instanceId.current}] Очищен предыдущий таймер автосохранения (из-за новых изменений)`);
    }
    
      console.log(`[Канбан ${instanceId.current}] Запускаем таймер автосохранения (1500ms) из-за изменений`);
      // showStatusIndicator("Сохранение...", 2000); // <-- УДАЛЕНО
    
      // Устанавливаем новый таймер
    autoSaveTimerRef.current = setTimeout(() => {
        const currentComponentId = instanceId.current;
        if (!currentComponentId || !boardState || !fileId) {
          console.error(`[Канбан] Невозможно сохранить: отсутствуют ID или состояние.`);
          // showStatusIndicator("Ошибка: нет данных для сохранения", 3000); // <-- УДАЛЕНО
          // ...
          return;
        }
        
        console.log(`[Канбан ${currentComponentId}] Сработал таймер автосохранения, сохраняем состояние...`);
        setIsSaving(true);
        lastSaveTimeRef.current = Date.now(); // Обновляем время последнего сохранения
        
        // --- ВАЖНО: Обновляем атрибут data-state ПЕРЕД сохранением ---
        if (updateAttributes && typeof updateAttributes === 'function') {
          try {
            const stateToUpdate = JSON.parse(JSON.stringify(boardState)); // Свежая копия
            console.log(`[Канбан ${currentComponentId}] Обновление data-state в редакторе ПЕРЕД сохранением.`);
            updateAttributes({
              'data-state': stateToUpdate,
              // Обновляем счетчик прямо здесь, если он есть
              'update-counter': (typeof stateToUpdate.updateCounter === 'number' ? stateToUpdate.updateCounter + 1 : 1)
            });
            // Обновляем и внутреннее состояние счетчиком, чтобы быть консистентным
            // (хотя saveStateToServer тоже его обновит перед отправкой)
            if (typeof boardState.updateCounter === 'number') {
                 boardState.updateCounter += 1;
             } else {
                 boardState.updateCounter = 1;
             }
            
          } catch (error) {
             console.error(`[Канбан ${currentComponentId}] Ошибка при обновлении data-state перед сохранением:`, error);
             // Продолжаем сохранение, но логируем ошибку
          }
        } else {
           console.warn(`[Канбан ${currentComponentId}] updateAttributes не является функцией, data-state не будет обновлен перед сохранением.`);
        }
        // --- КОНЕЦ ВАЖНОГО БЛОКА ---

        saveStateToServer(boardState, currentComponentId)
          .then(success => {
            console.log(`[Канбан ${currentComponentId}] Результат сохранения: ${success ? 'успешно' : 'ошибка'}`);
            if (success) {
              lastSavedState.current = JSON.parse(JSON.stringify(boardState));
              setHasUnsavedChanges(false);
              // showStatusIndicator("Сохранено", 1000); // <-- УДАЛЕНО
            } else {
              // showStatusIndicator("Ошибка сохранения", 3000); // <-- УДАЛЕНО
              // setHasUnsavedChanges(true);
            }
          })
          .catch(error => {
            console.error(`[Канбан ${currentComponentId}] Ошибка в Promise при сохранении:`, error);
            // showStatusIndicator("Ошибка сохранения", 3000); // <-- УДАЛЕНО
            // setHasUnsavedChanges(true);
          })
          .finally(() => {
            setIsSaving(false);
            autoSaveTimerRef.current = null;
            console.log(`[Канбан ${currentComponentId}] Сохранение завершено, сбрасываем isSaving и таймер`);
          });
      }, 1500); 
      } else {
      // Если нет несохраненных изменений или идет сохранение, или нет состояния/ID
      // console.log(`[Канбан ${instanceId.current}] Пропускаем запуск таймера: hasUnsavedChanges=${hasUnsavedChanges}, isSaving=${isSaving}, hasBoardState=${!!boardState}, hasInstanceId=${!!instanceId.current}`);
      }
    
    // Очистка таймера при размонтировании или изменении зависимостей
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        console.log(`[Канбан ${instanceId.current}] Очищен таймер автосохранения при размонтировании/изменении`);
      }
      // --- УДАЛЯЕМ ОЧИСТКУ ТАЙМЕРА ИНДИКАТОРА ---
      // if (savingIndicatorTimeoutRef.current) {
      //   clearTimeout(savingIndicatorTimeoutRef.current);
      // }
      // --- КОНЕЦ УДАЛЕНИЯ ---
    };
  // Убираем showStatusIndicator из зависимостей
  }, [hasUnsavedChanges, isSaving, boardState, updateAttributes, instanceId, fileId]); 

  // Обработчик изменения состояния канбана (вызывается из addCard, moveCard и т.д.)
  const handleStateChange = useCallback((newState: BoardState) => {
    // Сравниваем новое состояние с последним сохраненным
    const changesDetected = JSON.stringify(newState) !== JSON.stringify(lastSavedState.current);
    console.log(`[Канбан ${instanceId.current}] handleStateChange. Изменения обнаружены: ${changesDetected}`);

    // Обновляем состояние компонента
    setBoardState(newState);

    if (changesDetected) {
      setHasUnsavedChanges(true); // Помечаем, что есть несохраненные изменения

      // Диспатчим событие для useAutoSave, чтобы он НЕ сохранял ВЕСЬ документ
      if (editor) {
        console.log(`[Канбан ${instanceId.current}] Диспатч события kanban-state-change`);
        editor.view.dom.dispatchEvent(new CustomEvent('kanban-state-change', {
          bubbles: true,
          detail: { componentId: instanceId.current }
        }));
      }
    }
  // Не добавляем updateAttributes сюда, чтобы избежать лишних перерисовок/циклов
  }, [editor, instanceId]); 

  // Локальное обновление состояния без сохранения в редактор
  const updateLocalState = useCallback((newState: BoardState) => {
    console.log(`[Канбан ${instanceId.current}] Обновляем локальное состояние`);
    
    // Дополнительная защита от обновления с некорректным ID
    if (!instanceId.current) {
      console.warn(`[Канбан] Попытка обновить состояние без ID канбана`);
      return;
    }
    
    // Проверка корректности newState
    if (!newState || !newState.columns || !newState.cards || !newState.columnOrder) {
      console.error(`[Канбан ${instanceId.current}] Попытка обновить состояние с некорректными данными:`, newState);
      return; // Не обновляем состояние с некорректными данными
    }
    
    // Отладочное логирование для карточек
    const cardCount = Object.keys(newState.cards).length;
    console.log(`[Канбан ${instanceId.current}] Карточек в новом состоянии: ${cardCount}`);
    if (cardCount > 0) {
      // Логируем первые несколько карточек для проверки
      const cardSample = Object.keys(newState.cards).slice(0, 3);
      console.log(`[Канбан ${instanceId.current}] Пример карточек:`, 
        cardSample.map(id => ({id, title: newState.cards[id].title})));
    }
    
    // Глубокое клонирование для избежания ссылочных проблем
    const stateClone = JSON.parse(JSON.stringify(newState));
    
    // Добавляем дополнительные метаданные для диагностики
    stateClone._lastUpdate = new Date().toISOString();
    
    setBoardState(stateClone);
    
    // Проверяем, есть ли изменения по сравнению с последним сохраненным состоянием
    if (!areStatesEqual(stateClone, lastSavedState.current)) {
      console.log(`[Канбан ${instanceId.current}] Обнаружены изменения состояния`);
      setHasUnsavedChanges(true);
    } else {
      console.log(`[Канбан ${instanceId.current}] Изменений состояния не обнаружено`);
      setHasUnsavedChanges(false);
    }
  }, [instanceId]);

  // Перемещение карточки между колонками
  const moveCard = (cardId: string, sourceColumnId: string, destinationColumnId: string) => {
    if (!boardState) return;
    const sourceColumn = boardState.columns.find(col => col.id === sourceColumnId);
    const destinationColumn = boardState.columns.find(col => col.id === destinationColumnId);

    if (!sourceColumn || !destinationColumn) return;

    const updatedState = JSON.parse(JSON.stringify(boardState)); // Глубокая копия

    // Удаляем из исходной колонки
    const sourceColumnIndex = updatedState.columns.findIndex((col: Column) => col.id === sourceColumnId); // Добавлен тип Column
    if (sourceColumnIndex === -1) return; // Добавлена проверка
    const sourceCardIds = updatedState.columns[sourceColumnIndex].cardIds;
    const cardIndex = sourceCardIds.findIndex((id: string) => id === cardId); // Добавлен тип string
    if (cardIndex > -1) {
      sourceCardIds.splice(cardIndex, 1);
    }

    // Добавляем в целевую колонку
    const destinationColumnIndex = updatedState.columns.findIndex((col: Column) => col.id === destinationColumnId); // Добавлен тип Column
    if (destinationColumnIndex === -1) return; // Добавлена проверка
    updatedState.columns[destinationColumnIndex].cardIds.push(cardId);

    console.log(`[Канбан ${instanceId.current}] Карточка ${cardId} перемещена из ${sourceColumnId} в ${destinationColumnId}`);
    handleStateChange(updatedState); // Используем новую функцию
  };
  
  // Изменение заголовка доски
  const handleBoardTitleChange = (newTitle: string) => {
    if (!boardState) return;
    const updatedState = { ...boardState, boardTitle: newTitle };
    handleStateChange(updatedState); // Вызываем с новым состоянием
  };

  // Обработчик потери фокуса с заголовка доски (для сохранения)
  const handleTitleBlur = () => {
    // Здесь не нужно ничего делать с состоянием, 
    // handleStateChange уже был вызван в handleBoardTitleChange
    // console.log(`[Канбан ${instanceId.current}] Заголовок доски потерял фокус`);
  };

  // Изменение дедлайна карточки
  const handleDeadlineChange = (cardId: string, newDeadline: string) => {
    if (!boardState) return;
    if (!boardState.cards[cardId]) return; // Проверка на существование карточки

    const updatedState = JSON.parse(JSON.stringify(boardState)); // Глубокая копия
    updatedState.cards[cardId].deadline = newDeadline;
    
    handleStateChange(updatedState); // Вызываем с новым состоянием
  };

  // Изменение заголовка карточки
  const handleCardTitleChange = (cardId: string, newTitle: string) => {
    if (!boardState) return;
    if (!boardState.cards[cardId]) return; // Проверка

    const updatedState = JSON.parse(JSON.stringify(boardState)); // Глубокая копия
    updatedState.cards[cardId].title = newTitle;

    handleStateChange(updatedState); // Вызываем с новым состоянием
  };

  // Изменение описания карточки
  const handleCardDescriptionChange = (cardId: string, newDescription: string) => {
    if (!boardState) return;
    if (!boardState.cards[cardId]) return; // Проверка

    const updatedState = JSON.parse(JSON.stringify(boardState)); // Глубокая копия
    updatedState.cards[cardId].description = newDescription;

    handleStateChange(updatedState); // Вызываем с новым состоянием
  };

  // Изменение приоритета карточки
  const handleCardPriorityChange = (cardId: string, newPriority: 'low' | 'medium' | 'high' | undefined) => {
    if (!boardState) return;
    if (!boardState.cards[cardId]) return; // Проверка

    const updatedState = JSON.parse(JSON.stringify(boardState)); // Глубокая копия
    updatedState.cards[cardId].priority = newPriority;

    handleStateChange(updatedState); // Вызываем с новым состоянием
  };

  // Изменение заголовка колонки
  const handleColumnTitleChange = (columnId: string, newTitle: string) => {
    if (!boardState) return;
    const columnIndex = boardState.columns.findIndex((col: Column) => col.id === columnId); // Добавлен тип
    if (columnIndex === -1) return; // Проверка

    const updatedState = JSON.parse(JSON.stringify(boardState)); // Глубокая копия
    updatedState.columns[columnIndex].title = newTitle;

    handleStateChange(updatedState); // Вызываем с новым состоянием
  };

  // --- ВОССТАНАВЛИВАЕМ handleAddCard --- 
  const handleAddCard = (columnId: string) => {
    if (!boardState) return;
    const newCardId = `card-id-${Math.random().toString(16).slice(2)}`;
    const newCard: Card = {
      id: newCardId,
      title: '',
      description: '',
      priority: 'medium',
      deadline: '',
    };

    const updatedState = JSON.parse(JSON.stringify(boardState)); // Глубокая копия

    // Добавляем карточку в общий список
    updatedState.cards[newCardId] = newCard;

    // Добавляем ID карточки в колонку
    const targetColumnIndex = updatedState.columns.findIndex((col: Column) => col.id === columnId); // Добавлен тип
    if (targetColumnIndex !== -1) { // Исправлена проверка
      updatedState.columns[targetColumnIndex].cardIds.push(newCardId);
      console.log(`[Канбан ${instanceId.current}] Добавлена карточка ${newCardId} в колонку ${columnId}`);
      handleStateChange(updatedState); // Используем новую функцию
    } else {
       console.error(`[Канбан ${instanceId.current}] Не удалось найти колонку ${columnId} для добавления карточки`);
    }
  };
  // --- КОНЕЦ ВОССТАНОВЛЕНИЯ handleAddCard --- 

  // --- ВОССТАНАВЛИВАЕМ handleDeleteCard --- 
  const handleDeleteCard = (columnId: string, cardId: string) => {
    if (!boardState) return;
    const updatedState = JSON.parse(JSON.stringify(boardState)); // Глубокая копия

    // Удаляем карточку из общего списка
    if (updatedState.cards[cardId]) { // Проверяем наличие перед удалением
      delete updatedState.cards[cardId];
    } else {
      console.warn(`[Канбан ${instanceId.current}] Попытка удалить несуществующую карточку: ${cardId}`);
      return; // Не вызываем handleStateChange, если карточки не было
    }

    // Удаляем ID карточки из колонки
    const targetColumnIndex = updatedState.columns.findIndex((col: Column) => col.id === columnId); // Добавлен тип
    if (targetColumnIndex !== -1) {
      const initialLength = updatedState.columns[targetColumnIndex].cardIds.length;
      updatedState.columns[targetColumnIndex].cardIds = updatedState.columns[targetColumnIndex].cardIds.filter((id: string) => id !== cardId); // Добавлен тип
      if (updatedState.columns[targetColumnIndex].cardIds.length === initialLength) {
         console.warn(`[Канбан ${instanceId.current}] Карточка ${cardId} не найдена в колонке ${columnId} при удалении`);
      }
    } else {
       console.warn(`[Канбан ${instanceId.current}] Не удалось найти колонку ${columnId} при удалении карточки ${cardId}`);
    }

    console.log(`[Канбан ${instanceId.current}] Удалена карточка ${cardId} из колонки ${columnId}`);
    handleStateChange(updatedState); // Используем новую функцию
  };
  // --- КОНЕЦ ВОССТАНОВЛЕНИЯ handleDeleteCard --- 

  // Функция фильтрации карточек в колонке (уже исправлено ранее)
  const filteredCards = (columnId: string, cardIds: string[]): Card[] => {
    if (!boardState || !boardState.cards) return [];
    
    // console.log(`[Канбан ${instanceId.current}] Фильтрация карточек для колонки ${columnId}, найдено карточек: ${cardIds.length}`);
    
    const cardsInColumn = (cardIds || []) // Добавлена проверка на null/undefined cardIds
      .map((id: string) => boardState.cards[id]) // Добавлен тип string
      .filter((card): card is Card => card !== undefined); // Убираем undefined, если карточка не найдена

    // console.log(`[Канбан ${instanceId.current}] Карточки в колонке ${columnId} до фильтрации: ${cardsInColumn.length}`);

    const filtered = cardsInColumn.filter((card: Card) => { // Добавлен тип Card
      const titleMatch = filterText ? card.title.toLowerCase().includes(filterText.toLowerCase()) : true;
      const priorityMatch = filterPriority ? card.priority === filterPriority : true;
      return titleMatch && priorityMatch;
    });
    
    // console.log(`[Канбан ${instanceId.current}] После фильтрации для колонки ${columnId} получено ${filtered.length} карточек`);
    
    return filtered;
  };

  // Функция добавления новой колонки
  const handleAddColumn = () => {
    if (!boardState) return;
    
    const newColumnId = `col-${generateId()}`;
    
    const updatedState = {
      ...boardState,
      columns: [
        ...boardState.columns,
        { id: newColumnId, title: 'Новая колонка', cardIds: [] }
      ],
      columnOrder: [...boardState.columnOrder, newColumnId],
    };
    
    // updateLocalState(newState); // updateLocalState больше не нужен
    handleStateChange(updatedState); // Вызываем с новым состоянием
  };
  
  // Функция удаления колонки
  const handleDeleteColumn = (columnId: string) => {
    if (!boardState || !boardState.columns || !boardState.columnOrder) return;
    
    // Нельзя удалить последнюю колонку
    if (boardState.columnOrder.length <= 1) return;
    
    // Находим колонку для удаления
    const column = boardState.columns.find((col: Column) => col.id === columnId); // Добавлен тип
    if (!column) return;
    
    // Массив ID карточек, которые нужно удалить
    const cardIdsToRemove = column.cardIds || [];
    
    // Создаем новые объекты для обновленного состояния
    const newColumns = boardState.columns.filter((col: Column) => col.id !== columnId); // Добавлен тип
    const newColumnOrder = boardState.columnOrder.filter((id: string) => id !== columnId); // Добавлен тип
    
    // Создаем копию карточек и удаляем карточки из удаляемой колонки
    const newCards = { ...boardState.cards };
    cardIdsToRemove.forEach((cardId: string) => { // Добавлен тип
      delete newCards[cardId];
    });
    
    // Обновляем состояние доски
    const updatedState = {
      ...boardState,
      columns: newColumns,
      columnOrder: newColumnOrder,
      cards: newCards
    };
    
    // updateLocalState(newState); // updateLocalState больше не нужен
    handleStateChange(updatedState); // Вызываем с новым состоянием
    
    // Сбрасываем состояние удаления столбца
    setDeleteModalVisible(false);
    setColumnToDelete(null);
  };

  // Функция для запроса подтверждения удаления столбца
  const confirmDeleteColumn = (columnId: string) => {
    // Находим колонку для отображения названия в модальном окне
    const column = boardState?.columns?.find((col: Column) => col.id === columnId); // Добавлен тип
    if (column) {
      setColumnToDelete(column);
      setDeleteModalVisible(true);
    }
  };

  // Функция отмены удаления столбца
  const cancelDeleteColumn = () => {
    setDeleteModalVisible(false);
    setColumnToDelete(null);
  };

  // Функция подтверждения удаления столбца из модального окна
  const handleConfirmDeleteColumn = () => {
    if (columnToDelete) {
      handleDeleteColumn(columnToDelete.id); // Вызывает основную логику удаления
    }
    setDeleteModalVisible(false);
    setColumnToDelete(null);
  };

  // Функция установки столбца как завершающего для процента выполнения
  const handleSetAsCompletedColumn = (columnId: string) => {
    if (!boardState || !boardState.columns) return;
    
    // Создаем новые колонки с обновленной меткой завершения
    const updatedColumns = boardState.columns.map((col: Column) => ({ // Добавлен тип
      ...col,
      isCompletedColumn: col.id === columnId
    }));
    
    // Обновляем состояние доски
    const updatedState = {
      ...boardState,
      columns: updatedColumns
    };
    
    // updateLocalState(newState); // updateLocalState больше не нужен
    handleStateChange(updatedState); // Вызываем с новым состоянием
  };

  // Статистика по столбцам
  const calculateColumnStats = () => {
    // Проверяем существование boardState и его свойств
    if (!boardState || !boardState.cards || !boardState.columns) {
      return { columnStats: [], totalTasks: 0, completionPercentage: 0 };
    }
    
    const total = Object.keys(boardState.cards).length;
    const completedColumn = boardState.columns.find((col: Column) => col.isCompletedColumn);
    const completed = completedColumn?.cardIds?.length || 0;
    const completionPercentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    
    const columnStats = boardState.columns.map((col: Column) => ({
      id: col.id,
      title: col.title,
      count: col.cardIds?.length || 0
    }));
    
    return { columnStats, totalTasks: total, completionPercentage };
  };

  // Безопасно вычисляем статистику
  const { columnStats, totalTasks, completionPercentage } = calculateColumnStats();

  // Сохранение при выходе
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        // Для некоторых браузеров нужно добавить это сообщение
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Добавляем стили для канбан-доски и анимаций при монтировании
  useEffect(() => {
    // Проверяем, не существует ли уже стиль
    if (!document.getElementById(styleId.current)) {
      const style = document.createElement('style');
      style.id = styleId.current;
      style.setAttribute('data-kanban-cleanup', 'true');
      style.innerHTML = `
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideIn {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        /* Медиа-запрос для мобильных устройств */
        @media (max-width: 640px) {
          .fixed.inset-0.z-\\[9999\\] {
            padding: 12px !important;
          }
          .fixed.inset-0.z-\\[9999\\] > div {
            width: calc(100% - 20px) !important;
            max-width: 100% !important;
          }
        }
      `;
      document.head.appendChild(style);
    }
    
    // Устанавливаем идентификатор доски для DOM-элемента
    setTimeout(() => {
      const domNode = document.querySelector(`[data-kanban-board="true"]`);
      if (domNode) {
        domNode.setAttribute('data-board-id', `kanban-${instanceId.current}`);
        console.log(`[${instanceId.current}] Установлен data-board-id для DOM-элемента`);
      }
    }, 0);
    
    // Удаляем стиль при размонтировании
    return () => {
      const styleElement = document.getElementById(styleId.current);
      if (styleElement && styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
    };
  }, [styleId, instanceId]);
  
  // Очистка ресурсов при размонтировании
  useEffect(() => {
    // Создаем функцию для безопасного удаления созданных элементов
    const cleanup = () => {
      try {
        // Удаляем созданные стили при размонтировании
        const styleElements = document.querySelectorAll('style[data-kanban-cleanup]');
        styleElements.forEach(elem => {
          if (elem && elem.parentNode) {
            elem.parentNode.removeChild(elem);
          }
        });

        // Убеждаемся, что все порталы правильно размонтированы
        if (deleteModalVisible) {
          setDeleteModalVisible(false);
        }
      } catch (err) {
        console.warn(`[${instanceId.current}] Ошибка при очистке ресурсов канбан-доски:`, err);
      }
    };

    // Возвращаем функцию очистки
    return cleanup;
  }, [deleteModalVisible, instanceId]);
  
  // Блокируем прокрутку страницы при открытии модального окна
  useEffect(() => {
    if (deleteModalVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    // Очищаем эффект при размонтировании
    return () => {
      document.body.style.overflow = '';
    };
  }, [deleteModalVisible]);

  // Начало перетаскивания карточки
  const handleMoveToNextColumn = (cardId: string, currentColumnId: string) => {
    const currentColumnIndex = boardState?.columnOrder?.indexOf(currentColumnId);
    if (currentColumnIndex === undefined || currentColumnIndex === -1) return;
    
    const nextColumnIndex = currentColumnIndex + 1;
    if (nextColumnIndex >= (boardState?.columnOrder?.length || 0)) return;
    
    const nextColumnId = boardState?.columnOrder?.[nextColumnIndex];
    if (nextColumnId) {
      moveCard(cardId, currentColumnId, nextColumnId);
    }
  };

  const handleDragStart = (cardId: string, sourceColumnId: string, e: React.DragEvent<HTMLDivElement>) => {
    setDraggedCardId(cardId);
    setDraggedFromColumnId(sourceColumnId);
    
    // Добавляем данные в dataTransfer для обработки при drop
    e.dataTransfer.setData('application/kanban', JSON.stringify({
      cardId,
      sourceColumnId
    }));
    
    // Устанавливаем вид курсора
    e.dataTransfer.effectAllowed = 'move';
  };
  
  // Обработка событий drop
  const handleDrop = (destinationColumnId: string, e?: React.DragEvent<HTMLDivElement>) => {
    try {
      if (!draggedCardId || !draggedFromColumnId) return;
      
      // Проверяем, что карточка перемещается в другую колонку,
      // иначе ничего не делаем, чтобы избежать проблемы с блокировкой
      if (draggedFromColumnId !== destinationColumnId) {
        moveCard(draggedCardId, draggedFromColumnId, destinationColumnId);
      }
      
      // Сбрасываем состояние перетаскивания в любом случае
      setDraggedCardId(null);
      setDraggedFromColumnId(null);
    } catch (error) {
      console.error(`[${instanceId.current}] Ошибка при обработке drop:`, error);
      // Сбрасываем состояние в случае ошибки
      setDraggedCardId(null);
      setDraggedFromColumnId(null);
    }
  };

  // Обработчик события DragOver для всего канбана 
  // для предотвращения нежелательных вставок текста
  const handleWrapperDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    // Необходимо для разрешения drop
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move'; // Устанавливаем эффект перемещения
    // Предотвращаем обработку dataTransfer как текста
    const dt = e.dataTransfer;
    const hasKanbanData = dt.types.includes('application/tiptap-node');
    if (hasKanbanData) {
      try {
        const jsonData = dt.getData('application/tiptap-node');
        const data = JSON.parse(jsonData);
        if (data.type === 'kanbanBoard') {
          // Это перетаскивание канбана - предотвращаем обработку как текста
          console.log('[Канбан] Обнаружено перетаскивание канбана - предотвращаем вставку текста');
          // Если это тот же канбан - игнорируем перемещение
          if (data.attrs && data.attrs.id === instanceId.current) {
            e.dataTransfer.dropEffect = 'none'; // Запрещаем перетаскивание
          }
        }
      } catch (err) {
        // Игнорируем ошибки парсинга
      }
    }
  }, []);
  
  // Обработчик события Drop для всего канбана
  // для предотвращения нежелательных вставок текста
  const handleWrapperDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    // Если это наше перетаскивание канбана, обрабатываем его через плагин,
    // но предотвращаем вставку текста в редактор
    const dt = e.dataTransfer;
    const hasKanbanData = dt.types.includes('application/tiptap-node');
    if (hasKanbanData) {
      try {
        const jsonData = dt.getData('application/tiptap-node');
        const data = JSON.parse(jsonData);
        if (data.type === 'kanbanBoard') {
          // Это перетаскивание канбана
          console.log('[Канбан] Перехвачено событие drop для канбана');
          // Если это тот же канбан - предотвращаем обработку
          if (data.attrs && data.attrs.id === instanceId.current) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }
        }
      } catch (err) {
        // Игнорируем ошибки парсинга
      }
    }
  }, []);

  // Инициализация Kanban, если это необходимо
  useEffect(() => {
    console.log(`[Канбан ${instanceId.current}] useEffect для инициализации канбана`);
    
    // Если уже есть boardState, значит уже инициализировали
    // Убрали проверку на Object.keys(boardState.cards || {}).length > 0 для повторной инициализации при необходимости
    // if (boardState && Object.keys(boardState.cards || {}).length > 0) {
    //   console.log(`[Канбан ${instanceId.current}] Уже инициализирован, пропускаем инициализацию`);
    //   return;
    // }
    
    // Получаем HTML атрибуты из узла
    const nodeAttrs = node.attrs || {};
    console.log(`[Канбан ${instanceId.current}] Получены атрибуты узла:`, Object.keys(nodeAttrs));
    
    // Пытаемся получить data-state из атрибутов узла
    let loadState: BoardState | null = null;
    
    try {
      // Проверяем, есть ли строка data-state в аттрибутах
      if (nodeAttrs['data-state']) {
        console.log(`[Канбан ${instanceId.current}] Найден атрибут data-state, тип:`, typeof nodeAttrs['data-state']);
        
        if (typeof nodeAttrs['data-state'] === 'string') {
          // Если это строка - логируем первые и последние символы для диагностики
          const stateStr = nodeAttrs['data-state'];
          console.log(`[Канбан ${instanceId.current}] data-state - строка длиной ${stateStr.length} символов`);
          console.log(`[Канбан ${instanceId.current}] Начало: ${stateStr.substring(0, 50)}...`);
          console.log(`[Канбан ${instanceId.current}] Конец: ...${stateStr.substring(stateStr.length - 50)}`);
          
          // Проверяем наличие дублирования структуры JSON
          const openingBraces = (stateStr.match(/{/g) || []).length;
          const closingBraces = (stateStr.match(/}/g) || []).length;
          console.log(`[Канбан ${instanceId.current}] Количество открывающих скобок: ${openingBraces}, закрывающих: ${closingBraces}`);
          
          if (openingBraces !== closingBraces) {
            console.warn(`[Канбан ${instanceId.current}] Обнаружено несоответствие скобок в JSON! Возможно, данные повреждены.`);
          }
          
          try {
            // Пытаемся распарсить строку с дополнительной обработкой возможных дублирований JSON
            loadState = cleanAndParseState(nodeAttrs['data-state']);
            console.log(`[Канбан ${instanceId.current}] Успешно распарсили строку data-state в объект`);
            
            // Проверка корректности структуры состояния
            if (loadState && isValidBoardState(loadState)) {
              console.log(`[Канбан ${instanceId.current}] Состояние прошло валидацию структуры`);
              console.log(`[Канбан ${instanceId.current}] Карточек в загруженном состоянии:`, Object.keys(loadState.cards).length);
            } else {
              console.warn(`[Канбан ${instanceId.current}] Загруженное состояние не прошло валидацию, нужно создать новое`);
              loadState = null;
            }
          } catch (e) {
            console.error(`[Канбан ${instanceId.current}] Ошибка при парсинге строки data-state:`, e);
            console.error(`[Канбан ${instanceId.current}] Проблемная строка data-state (первые 100 символов):`, nodeAttrs['data-state'].substring(0, 100) + '...');
            loadState = null;
          }
        } else if (typeof nodeAttrs['data-state'] === 'object' && nodeAttrs['data-state'] !== null) {
          // Если это уже объект, просто используем его
          console.log(`[Канбан ${instanceId.current}] data-state уже является объектом, анализируем его структуру`);
          const rawState = nodeAttrs['data-state'];
          
          // Проверяем наличие ключевых полей
          if (rawState.columns && rawState.cards && rawState.columnOrder) {
            console.log(`[Канбан ${instanceId.current}] Объект data-state имеет корректную структуру`);
            loadState = rawState as BoardState;
            
            // Проверка корректности структуры состояния
            if (isValidBoardState(loadState)) {
              console.log(`[Канбан ${instanceId.current}] Состояние прошло валидацию структуры`);
              console.log(`[Канбан ${instanceId.current}] Карточек в загруженном состоянии:`, Object.keys(loadState.cards).length);
            } else {
              console.warn(`[Канбан ${instanceId.current}] Загруженное состояние не прошло валидацию, нужно создать новое`);
              loadState = null;
            }
          } else {
            console.warn(`[Канбан ${instanceId.current}] Объект data-state не имеет необходимых полей`);
            loadState = null;
          }
        } else {
          console.warn(`[Канбан ${instanceId.current}] data-state имеет неожиданный тип:`, typeof nodeAttrs['data-state']);
          loadState = null;
        }
      } else {
        console.log(`[Канбан ${instanceId.current}] Не найден атрибут data-state, будет создано новое состояние`);
      }
    } catch (error) {
      console.error(`[Канбан ${instanceId.current}] Ошибка при инициализации:`, error);
      loadState = null;
    }
    
    // Если не удалось загрузить состояние, создаем новое
    if (!loadState) {
      console.log(`[Канбан ${instanceId.current}] Создаем новое состояние канбана`);
      loadState = emptyBoardState; // Исправлена ошибка вызова
    }
    
    // Если PJSON флаг включен, добавляем тестовую карточку, если канбан пустой
    if (PJSON_FLAG && loadState && Object.keys(loadState.cards).length === 0) {
      console.log(`[Канбан ${instanceId.current}] Добавляем тестовую карточку для PJSON`);
      
      // Генерируем уникальные ID для тестовой карточки
      const testCardId = `card-test-id-${Math.random().toString(36).substr(2, 9)}`;
      
      // Добавляем тестовую карточку в первую колонку
      if (loadState) {
        loadState.cards[testCardId] = {
          id: testCardId,
          title: 'Тестовая карточка',
          description: 'Описание тестовой карточки',
          priority: 'medium',
          deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        };
        
        // Добавляем ID карточки в массив карточек первой колонки
        if (loadState.columns.length > 0) {
          const firstColumn = loadState.columns[0];
          firstColumn.cardIds.push(testCardId);
        }
      }
    }
    
    // Проверка: логируем количество карточек в полученном состоянии
    const cardCount = loadState ? Object.keys(loadState.cards || {}).length : 0;
    console.log(`[Канбан ${instanceId.current}] Всего карточек в состоянии перед setBoardState: ${cardCount}`);
    
    // Устанавливаем состояние канбана
    if (loadState) {
      // Используем состояние как есть, т.к. removeTestCards удалена
      const cleanState = loadState; 
      
      console.log(`[Канбан ${instanceId.current}] Установлено состояние boardState, карточек: ${Object.keys(cleanState.cards || {}).length}`);
      setBoardState(cleanState);
      lastSavedState.current = JSON.parse(JSON.stringify(cleanState)); // Сохраняем начальное состояние как последнее
      
      // Если состояние cleanState отличается от исходного loadState (что теперь маловероятно, т.к. мы не вызываем removeTestCards)
      if (updateAttributes && typeof updateAttributes === 'function' && 
          JSON.stringify(cleanState) !== JSON.stringify(loadState)) {
        console.log(`[Канбан ${instanceId.current}] Обновляем data-state, так как cleanState отличается от loadState`);
        updateAttributes({
          'data-state': cleanState
        });
      }
    }
    
    // Если в редакторе до этого был data-type="kanban", исправляем на "kanbanBoard"
    if (nodeAttrs['data-type'] === 'kanban' && updateAttributes && typeof updateAttributes === 'function') {
      console.log(`[Канбан ${instanceId.current}] Обновляем атрибут data-type с kanban на kanbanBoard`);
      
      // Создаем копию состояния перед обновлением атрибутов
      const stateCopy = JSON.parse(JSON.stringify(loadState || {}));
      
      updateAttributes({
        'data-type': 'kanbanBoard',
        'data-state': stateCopy // Обновляем data-state, чтобы гарантировать корректный формат
      });
    }
  }, [node, instanceId, updateAttributes]);

  // Вспомогательная функция для очистки и парсинга поврежденного JSON
  const cleanAndParseState = (stateStr: string): BoardState => {
    try {
      console.log(`[Канбан JSON] Пытаемся очистить и распарсить строку длиной ${stateStr.length}`);
      
      // Попытка очистить повреждённый JSON
      let cleanJsonStr = stateStr;
      
      // Ищем первую полную JSON структуру, игнорируя возможные дублирования
      const jsonStartPos = cleanJsonStr.indexOf('{');
      if (jsonStartPos > -1) {
        let openBraces = 0;
        let foundValidJson = false;
        let endPos = jsonStartPos;
        
        // Анализируем символы, чтобы найти корректное закрытие JSON
        for (let i = jsonStartPos; i < cleanJsonStr.length; i++) {
          if (cleanJsonStr[i] === '{') openBraces++;
          else if (cleanJsonStr[i] === '}') {
            openBraces--;
            // Когда нашли закрывающую скобку для начального JSON объекта
            if (openBraces === 0) {
              endPos = i + 1; // включая закрывающую скобку
              foundValidJson = true;
              break;
            }
          }
        }
        
        if (foundValidJson) {
          // Извлекаем только валидную часть JSON
          cleanJsonStr = cleanJsonStr.substring(jsonStartPos, endPos);
          console.log(`[Канбан JSON] Извлечена валидная часть JSON длиной ${cleanJsonStr.length} символов`);
        }
      }
      
      // Теперь пробуем парсить очищенную строку
      return JSON.parse(cleanJsonStr);
    } catch (e) {
      console.error(`[Канбан JSON] Ошибка при очистке и парсинге JSON:`, e);
      throw e; // Пробрасываем ошибку дальше
    }
  };

  // Функция для удаления тестовых карточек из загруженного состояния
  const removeTestCards = (state: BoardState): BoardState => {
    if (!state) return state;

    // Создаем копию состояния
    const cleanState = JSON.parse(JSON.stringify(state));
    let testCardsRemoved = 0;

    // Идентифицируем тестовые карточки (начинающиеся с "card-test-id-" или "card-test-")
    const testCardIds = Object.keys(cleanState.cards || {}).filter((id: string) => // Явно указываем тип id
      id.startsWith('card-test-id-') || id.startsWith('card-test-')
    );
    
    if (testCardIds.length > 0) {
      console.log(`[Канбан ${instanceId.current}] Найдено ${testCardIds.length} тестовых карточек для удаления:`, testCardIds);
      
      // Удаляем тестовые карточки из объекта cards
      for (const cardId of testCardIds) {
        delete cleanState.cards[cardId];
        testCardsRemoved++;
      }
      
      // Удаляем ссылки на удаленные карточки из колонок
      for (const column of cleanState.columns) {
        const before = column.cardIds.length;
        column.cardIds = column.cardIds.filter((id: string) => !testCardIds.includes(id)); // Убедимся, что тип id указан
        if (before !== column.cardIds.length) {
          console.log(`[Канбан ${instanceId.current}] Из колонки "${column.title}" удалено ${before - column.cardIds.length} ссылок на тестовые карточки`);
        }
      }
      
      console.log(`[Канбан ${instanceId.current}] Удалено ${testCardsRemoved} тестовых карточек`);
    }
    
    return cleanState;
  };

  // Рендеринг компонента
  return (
    <NodeViewWrapper 
      className={`interactive-kanban-wrapper ${isDragging ? 'kanban-dragging' : ''}`}
      onMouseEnter={showControls}
      onMouseLeave={hideControls}
      onDragOver={handleWrapperDragOver}
      onDrop={handleWrapperDrop}
      data-kanban-board="true"
    >
      {/* Ручка для перетаскивания всего канбана */}
      {showDragHandle && !isDragging && (
        <div
          className="absolute top-2 right-10 w-6 h-6 bg-transparent dark:bg-transparent text-blue-500 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 hover:bg-transparent dark:hover:bg-transparent transition-all duration-500 z-10 cursor-grab active:cursor-grabbing drag-handle"
          style={{
            animation: 'fadeIn 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards, scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
            transformOrigin: 'center'
          }}
          title="Перетащить канбан-доску"
          draggable="true"
          onDragStart={handleKanbanDragStart}
          onDragEnd={handleKanbanDragEnd}
          onMouseDown={(e) => {
            e.stopPropagation();
            // Добавляем класс при нажатии для визуальной обратной связи
            const target = e.currentTarget;
            target.classList.add('active:scale-95');
            console.log('[Канбан] Нажатие на иконку перетаскивания');
            
            // Устанавливаем атрибут, чтобы предотвратить обработку клика редактором
            document.body.setAttribute('data-kanban-dragging', 'true');
          }}
          onMouseUp={(e) => {
            // Удаляем класс при отпускании
            const target = e.currentTarget;
            target.classList.remove('active:scale-95');
            
            // Удаляем атрибут только если событие было завершено внутри редактора
            setTimeout(() => {
              document.body.removeAttribute('data-kanban-dragging');
            }, 100);
          }}
          onDragEnter={(e) => {
            console.log('[Канбан] DragEnter на канбане');
            e.preventDefault(); // Важно для корректной работы
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="9" x2="19" y2="9"></line>
            <line x1="5" y1="15" x2="19" y2="15"></line>
          </svg>
        </div>
      )}
      
      {/* Кнопка удаления канбана */}
      {showDeleteButton && !isDragging && (
        <button
          className="absolute top-2 right-2 w-6 h-6 bg-transparent dark:bg-transparent text-red-500 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 hover:bg-transparent dark:hover:bg-transparent transition-all duration-500 z-10"
          onClick={handleDelete}
          title="Удалить канбан-доску"
          style={{
            animation: 'fadeIn 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards, scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
            transformOrigin: 'center'
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          ✕
        </button>
      )}
      
      <div 
        className="kanban-board-react-content p-4 rounded-lg border border-gray-200 dark:border-gray-700 relative"
        // Добавляем relative для позиционирования будущих элементов управления
      >
        <div className="p-4 flex flex-col md:flex-row justify-between items-start md:items-center">
          <div className="text-lg font-medium text-gray-900 dark:text-white mb-2 md:mb-0 w-full">
            <input
              type="text"
              value={boardState?.boardTitle || "Канбан-доска"}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                e.preventDefault();
                e.stopPropagation();
                const newTitle = e.target.value;
                handleBoardTitleChange(newTitle);
              }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
              }}
              onMouseDown={(e: React.MouseEvent) => {
                e.stopPropagation();
              }}
              onKeyDown={(e: React.KeyboardEvent) => {
                e.stopPropagation();
                // Сохраняем при нажатии Enter
                if (e.key === 'Enter') {
                  (e.target as HTMLInputElement).blur();
                }
              }}
              onBlur={handleTitleBlur}
              className="kanban-board-title bg-transparent p-0 w-full text-lg font-bold text-gray-900 dark:text-white text-center"
              placeholder="Название доски"
            />
          </div>
        </div>

        <div className="px-4 pb-2">
          <div className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-4 space-y-3">
            <div className="flex gap-2 flex-wrap">
              <div className="stat-item text-xs bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300 py-1 px-2 rounded">
                <span className="font-medium">Всего задач:</span> {totalTasks}
              </div>
              {columnStats.map((column, index) => (
                <div 
                  key={column.id} 
                  className={`stat-item text-xs py-1 px-2 rounded ${
                    index % 4 === 0 ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' :
                    index % 4 === 1 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                    index % 4 === 2 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                    'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
                  }`}
                >
                  <span className="font-medium">{column.title}:</span> {column.count}
                </div>
              ))}
            </div>
            
            <div className="w-full sm:w-auto sm:flex sm:items-center">
              <div className="flex items-center justify-between mb-1 sm:mb-0 sm:mr-2">
                <span className="text-xs text-gray-500 dark:text-gray-400 sm:mr-2">Прогресс выполнения</span>
                <span className="text-xs text-gray-500 dark:text-gray-400 sm:hidden">{completionPercentage}%</span>
              </div>
              <div className="w-full sm:w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden sm:mr-2">
                <div className="bg-green-500 h-full rounded-full transition-all duration-300" style={{ width: `${completionPercentage}%` }}></div>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">{completionPercentage}%</span>
            </div>
          </div>
        </div>
        
        <div className="kanban-columns-container overflow-x-auto pb-2">
          <div className="inline-flex gap-4 p-4 w-max min-w-full">
            {boardState?.columnOrder?.map((columnId, index) => {
              const column = boardState?.columns?.find(c => c.id === columnId);
              if (!column) return null;

              // Фильтрованные карточки
              const cards = filteredCards(columnId, column.cardIds);
              // Первая колонка (Планируется)
              const isFirstColumn = index === 0;
              // Проверяем, является ли эта колонка завершающей
              const isCompletedColumn = column.isCompletedColumn || false;

              return (
                <div
                  key={column.id}
                  className="kanban-column rounded-lg p-3 border border-gray-200 dark:border-gray-700 min-w-[250px] flex-shrink-0 w-[300px]"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDrop(column.id, e);
                  }}
                >
                  <div className="kanban-column-header flex items-center mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                    <input
                      type="text"
                      value={column.title}
                      onChange={(e) => handleColumnTitleChange(column.id, e.target.value)}
                      className="text-sm font-medium text-gray-700 dark:text-gray-300 bg-transparent border-none p-0 focus:ring-0 mr-auto"
                      onMouseDown={e => e.stopPropagation()}
                      onKeyDown={e => e.stopPropagation()}
                    />
                    <div className="flex items-center">
                      {/* Кнопка добавления новой задачи */}
                      <button
                        className="flex items-center justify-center text-blue-500 hover:text-blue-600 transition-colors mx-1 w-6 h-6"
                        onClick={() => handleAddCard(column.id)}
                        onMouseDown={e => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                        title="Добавить задачу"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                      
                      {/* Кнопка отметки колонки как завершающей с новой иконкой */}
                      <button
                        className={`flex items-center justify-center transition-colors mx-1 rounded ${isCompletedColumn ? 
                          'text-green-500 hover:text-green-600 bg-green-50 dark:bg-green-900/20' : 
                          'text-gray-400 hover:text-green-500'}`}
                        onClick={() => handleSetAsCompletedColumn(column.id)}
                        onMouseDown={e => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                        title={isCompletedColumn ? "Эта колонка используется для расчета % завершения" : "Отметить как колонку завершения"}
                        style={{ width: '24px', height: '24px' }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-17l6 4 6-4 6 4v12l-6-4-6 4" />
                        </svg>
                      </button>
                      
                      {/* Кнопка удаления колонки (если это не единственная колонка) */}
                      {boardState?.columnOrder?.length > 1 && (
                        <button
                          className="flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors mx-1 w-6 h-6"
                          onClick={() => confirmDeleteColumn(column.id)}
                          onMouseDown={e => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                          title="Удалить колонку"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="min-h-[100px] kanban-cards-container pt-2">
                    {cards.map((card, cardIndex) => (
                      <DraggableCard
                        key={card.id}
                        card={card}
                        columnId={column.id}
                        handleDeleteCard={handleDeleteCard}
                        handleCardTitleChange={handleCardTitleChange}
                        handleCardDescriptionChange={handleCardDescriptionChange}
                        handleCardPriorityChange={handleCardPriorityChange}
                        handleDeadlineChange={handleDeadlineChange}
                        handleDragStart={handleDragStart}
                        isLastCard={cardIndex === cards.length - 1}
                        onMoveToNextColumn={handleMoveToNextColumn}
                        hasNextColumn={index < (boardState?.columnOrder?.length || 0) - 1}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
            
            {/* Кнопка добавления новой колонки */}
            <div className="flex items-center justify-center">
              <button
                className="flex items-center justify-center text-blue-500 hover:text-blue-600 transition-colors w-16 h-24 mx-2"
                onClick={() => handleAddColumn()}
                onMouseDown={e => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                title="Добавить колонку"
                              >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Модальное окно через портал для корректного отображения поверх остального контента */}
      {deleteModalVisible && columnToDelete && (
        ReactDOM.createPortal(
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              animation: 'fadeIn 0.2s ease-in-out'
            }}
            onMouseDown={e => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <div 
              className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full p-6 border border-gray-200 dark:border-gray-700"
              style={{ animation: 'slideIn 0.3s ease-out' }}
            >
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Удаление столбца</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Вы уверены, что хотите удалить столбец "{columnToDelete.title}"? 
                Все задачи внутри этого столбца будут также удалены.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-md transition-colors"
                  onClick={cancelDeleteColumn}
                  onMouseDown={e => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  Отмена
                </button>
                <button
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors"
                  onClick={handleConfirmDeleteColumn}
                  onMouseDown={e => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  Удалить
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      )}
      
      {/* Модальное окно подтверждения удаления канбана */}
      {showDeleteKanbanModal && (
        ReactDOM.createPortal(
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              animation: 'fadeIn 0.2s ease-in-out'
            }}
            onMouseDown={e => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <div 
              className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full p-6 border border-gray-200 dark:border-gray-700"
              style={{ animation: 'slideIn 0.3s ease-out' }}
            >
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Удаление канбан-доски</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Вы уверены, что хотите удалить эту канбан-доску? 
                Все столбцы и задачи будут удалены, это действие нельзя отменить.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-md transition-colors"
                  onClick={cancelDeleteKanban}
                  onMouseDown={e => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  Отмена
                </button>
                <button
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors"
                  onClick={confirmDeleteKanban}
                  onMouseDown={e => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  Удалить
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      )}
    </NodeViewWrapper>
  );
};

export default KanbanBoardComponent; 